import React, { useState, useMemo, useEffect } from 'react'
import './ApplyV2ProfileStep.scss'
import { Select } from '@chakra-ui/react'
import { useApplyV2Context } from '@core/utils/contexts/ApplyV2ModalContext/ApplyV2ModalContext'
import {
    usePutProfileV2ApplyMutation,
    useCheckEmailDuplicateMutation,
    useCheckPhoneDuplicateMutation,
} from '@core/app/api/applyJobV2Api'
import { GoogleAutocompleteStandalone, Values } from './GoogleMapsInput'
import PhoneInput from './PhoneInput';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next'

const ApplyV2ProfileStep = () => {
    const { t } = useTranslation()
    const [putProfile, { isLoading, isSuccess, isError }] = usePutProfileV2ApplyMutation()
    const [checkEmailDuplicate] = useCheckEmailDuplicateMutation();
    const [checkPhoneDuplicate] = useCheckPhoneDuplicateMutation();

    const { initData, settingsData } = useApplyV2Context()

    const [form, setForm] = useState<{
        firstName: string
        lastName: string
        email: string
        linkedIn: string
        phonePrefix: string
        phoneNumber: string
        gender: string
        dobDay: string
        dobMonth: string
        dobYear: string
        address: Values | null
    }>({
        firstName: '',
        lastName: '',
        email: '',
        linkedIn: '',
        phonePrefix: '',
        phoneNumber: '',
        gender: '',
        dobDay: '',
        dobMonth: '',
        dobYear: '',
        address: null,
    })


    const genderOptions = [
        { value: '1', label: t('68', 'Male') as string },
        { value: '2', label: t('69', 'Female') as string },
        { value: '3', label: t('1144', 'Other') as string },
        // { value: '4', label: 'Prefer not to say' }, // якщо треба
    ]

    const validateLinkedIn = (value: string) => {
        const normalized = value.trim().toLowerCase();
        return (
            normalized.startsWith('linkedin.com/') ||
            normalized.startsWith('www.linkedin.com/') ||
            normalized.startsWith('https://linkedin.com/') ||
            normalized.startsWith('https://www.linkedin.com/')
        );
    };

    const validateEmail = (value: string) => {
        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailPattern.test(value.trim());
    };
    const [showLoginButton, setShowLoginButton] = useState(false);
    const [emailError, setEmailError] = useState<string | null>(null);
    const [phoneError, setPhoneError] = useState<string | null>(null);
    const [showPhoneLoginButton, setShowPhoneLoginButton] = useState(false);

    const navigate = useNavigate();

    useEffect(() => {
        if (form.email && validateEmail(form.email)) {
            const debounceTimeout = setTimeout(() => {
                checkEmailDuplicate({ email: form.email.trim() })
                    .unwrap()
                    .then(result => {
                        if (result.data && result.data.isUnique === false) {
                            setShowLoginButton(true);
                            // 10184
                            setEmailError('This email is already registered. Log in with it or enter a different one.');
                        } else {
                            setShowLoginButton(false);
                            setEmailError(null);
                        }
                    })
                    .catch(() => {
                        setShowLoginButton(false);
                        setEmailError('An error occurred while checking the email.');
                    });
            }, 500);

            return () => clearTimeout(debounceTimeout);
        } else {
            setShowLoginButton(false);
            setEmailError(null);
        }
        // eslint-disable-next-line
    }, [form.email]);

    useEffect(() => {
        const prefix = form.phonePrefix.trim();
        const number = form.phoneNumber.trim();
        if (prefix && number && number.length >= 5) {
            const debounceTimeout = setTimeout(() => {
                checkPhoneDuplicate({ mobilePhone: { prefix, number } })
                    .unwrap()
                    .then(result => {
                        if (result.data && result.data.isUnique === false) {
                            setShowPhoneLoginButton(true);
                            // 10185
                            setPhoneError('This phone number is already registered. Log in with the registered email or enter a different number.');
                        } else {
                            setShowPhoneLoginButton(false);
                            setPhoneError(null);
                        }
                    })
                    .catch(() => {
                        setShowPhoneLoginButton(false);
                        setPhoneError('An error occurred while checking the phone.');
                    });
            }, 500);

            return () => clearTimeout(debounceTimeout);
        } else {
            setShowPhoneLoginButton(false);
            setPhoneError(null);
        }
        // eslint-disable-next-line
    }, [form.phonePrefix, form.phoneNumber]);


    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));

        if (name === 'linkedIn') {
            const isRequired = settingsData?.fields?.profile?.linkedin?.required;
            setErrors((prev) => ({
                ...prev,
                linkedIn:
                    (isRequired || value.trim() !== '') && !validateLinkedIn(value)
                        // 148
                        ? 'Invalid LinkedIn URL'
                        : undefined,
            }));
        }

        if (name === 'email') {
            const isRequired = settingsData?.fields?.profile?.email?.required;
            setErrors((prev) => ({
                ...prev,
                email:
                    (isRequired || value.trim() !== '') && !validateEmail(value)
                        //728 
                        ? 'Invalid email address'
                        : undefined,
            }));
        }
    };

    const [errors, setErrors] = useState<{
        linkedIn?: string;
        email?: string
    }>({})

    const handlePhoneInputChange = ({ prefix, number }: { prefix: string; number: string }) => {
        setForm(prev => ({
            ...prev,
            phonePrefix: prefix,
            phoneNumber: number,
        }));
    };

    useEffect(() => {
        const profile = initData?.data?.profile
        if (!profile) return

        const dob = profile.dateOfBirth?.split('-') ?? []

        setForm({
            firstName: profile.firstName || '',
            lastName: profile.lastName || '',
            email: profile.email || '',
            linkedIn: profile.linkedin || '',
            phonePrefix: profile.mobilePhone?.prefix || '',
            phoneNumber: profile.mobilePhone?.number || '',
            gender: profile.gender?.toString() ?? '',
            dobDay: dob[2] || '',
            dobMonth: dob[1] || '',
            dobYear: dob[0] || '',
            address: profile.address
                ? {
                    label: profile.address.city || '',
                    country: profile.address.country || '',
                    city: profile.address.city || '',
                    postalCode: profile.address.postalCode || '',
                    address1: profile.address.address1 || '',
                    address2: '',
                }
                : null,
        })
    }, [initData])


    const handleSubmit = async () => {

        if (!initData?.data?.uid) return

        const genderNum = Number(form.gender)
        const genderValue = [0, 1, 2, 3, 4].includes(genderNum)
            ? (genderNum as 0 | 1 | 2 | 3 | 4)
            : null

        const dob =
            form.dobYear && form.dobMonth && form.dobDay
                ? `${form.dobYear}-${form.dobMonth}-${form.dobDay}`
                : null

        const body = {
            firstName: form.firstName,
            lastName: form.lastName,
            email: form.email,
            linkedin:
                form.linkedIn.trim()
                    ? (
                        form.linkedIn.trim().startsWith('http')
                            ? form.linkedIn.trim()
                            : 'https://' + form.linkedIn.trim()
                    )
                    : undefined,
            gender: genderValue,
            dateOfBirth: dob !== '0000-00-00' ? dob : null,
            address: form.address
                ? {
                    country: form.address.country || '',
                    city: form.address.city || '',
                    postalCode: form.address.postalCode || '',
                    address1: form.address.address1 || '',
                    address2: '',
                }
                : null,
            mobilePhone:
                form.phonePrefix.trim() && form.phoneNumber.trim()
                    ? {
                        prefix: form.phonePrefix.trim(),
                        number: form.phoneNumber.trim(),
                    }
                    : null,
        }

        try {
            await putProfile({ uid: initData.data.uid, body })
        } catch (e) {
            console.error('Error saving profile', e)
        }
    }
    const isValid = useMemo(() => {
        if (!settingsData?.fields?.profile) return false;
        if (emailError || phoneError) return false;

        const valueMap: Record<string, any> = {
            firstName: form.firstName.trim(),
            lastName: form.lastName.trim(),
            email: form.email.trim(),
            linkedin: form.linkedIn.trim(),
            mobilePhone: form.phonePrefix.trim() && form.phoneNumber.trim(),
            gender: form.gender.trim(),
            dateOfBirth: form.dobYear && form.dobMonth && form.dobDay,
            address: form.address && form.address.address1,
        };

        const profileFields = Object.values(settingsData.fields.profile) as Array<{
            name: string;
            required: boolean;
            visible: boolean;
        }>;
        for (const field of profileFields) {
            if (field.required && field.visible) {
                if (field.name === 'mobilePhone') {
                    if (!valueMap.mobilePhone) return false;
                }
                else if (field.name === 'dateOfBirth') {
                    if (!(form.dobYear && form.dobMonth && form.dobDay)) return false;
                }
                else if (field.name === 'address') {
                    if (!form.address || !form.address.address1) return false;
                }
                else {
                    if (!valueMap[field.name]) return false;
                }
            }
        }
        return true;
    }, [form, settingsData]);

    return (
        <form className="ApplyV2ProfileStep">
            <div className="row two-cols">
                {settingsData?.fields?.profile?.firstName?.visible && (
                    <div className="field">
                        <label>{t('169', 'First name') as string}
                            {settingsData?.fields?.profile?.firstName?.required && <span className="required-star">*</span>}

                        </label>
                        <input
                            name="firstName"
                            value={form.firstName}
                            onChange={handleChange}
                            placeholder={t('169', 'Enter your first name') as string}
                        />
                    </div>
                )}
                {settingsData?.fields?.profile?.lastName?.visible && (
                    <div className="field">
                        <label>{t('170', 'Last name') as string}
                            {settingsData?.fields?.profile?.lastName?.required && <span className="required-star">*</span>}
                        </label>
                        <input
                            name="lastName"
                            value={form.lastName}
                            onChange={handleChange}
                            placeholder={t('170', 'Enter your last name') as string}
                        />
                    </div>
                )}
            </div>

            {settingsData?.fields?.profile?.email?.visible && (
                <div className="row">
                    <div className="field full">
                        <label>
                            {t('92', 'Email') as string}
                            {settingsData?.fields?.profile?.email?.required && <span className="required-star">*</span>}
                        </label>
                        <input
                            name="email"
                            type="email"
                            value={form.email}
                            onChange={handleChange}
                            placeholder={t('92', 'Enter your email') as string}
                            className={errors.email || emailError ? 'input-error' : ''}
                        />
                        {showLoginButton && emailError && (
                            <div className="login-error-row">
                                <svg
                                    className="error-icon"
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="20"
                                    height="20"
                                    fill="#e53e3e"
                                    viewBox="0 0 24 24"
                                >
                                    <circle cx="12" cy="12" r="10" fill="#e53e3e" />
                                    <path
                                        d="M12 8v4M12 16h.01"
                                        stroke="#fff"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        fill="none"
                                    />
                                </svg>
                                <span>{emailError}</span>
                                <button
                                    className="login-btn"
                                    type="button"
                                    onClick={() => navigate(`/login?email=${encodeURIComponent(form.email.trim())}`)}
                                >
                                    {t('1890', 'Log in') as string}
                                </button>
                            </div>
                        )}

                        {!showLoginButton && errors.email && (
                            <span className="error-text">
                                <svg className="error-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 24 24">
                                    <path
                                        stroke="#e53e3e"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M12 9v4M12 17h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9Z"
                                    />
                                </svg>
                                {errors.email}
                            </span>
                        )}
                    </div>
                </div>
            )}

            {settingsData?.fields?.profile?.linkedin?.visible && (

                <div className="row">
                    <div className="field full">
                        <label>{t('9691', 'LinkedIn profile link') as string}
                            {settingsData?.fields?.profile?.linkedin?.required && <span className="required-star">*</span>}
                        </label>
                        <input
                            name="linkedIn"
                            value={form.linkedIn}
                            onChange={handleChange}
                            placeholder="https://www.linkedin.com/in/username"
                            className={errors.linkedIn ? 'input-error' : ''}
                        />
                        {errors.linkedIn && (
                            <span className="error-text">
                                <svg
                                    className="error-icon"
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="16"
                                    height="16"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        stroke="#e53e3e"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M12 9v4M12 17h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9Z"
                                    />
                                </svg>
                                {errors.linkedIn}
                            </span>
                        )}
                    </div>
                </div>
            )}

            {settingsData?.fields?.profile?.mobilePhone?.visible && (
                <div className="row">
                    <div className="field full phone">
                        <label>{t('137', 'Mobile phone') as string}
                            {settingsData?.fields?.profile?.mobilePhone?.required && <span className="required-star">*</span>}
                        </label>
                        <PhoneInput
                            prefix={form.phonePrefix}
                            number={form.phoneNumber}
                            onChange={handlePhoneInputChange}
                            height={36}
                            slotStyles={{
                                number: {
                                    height: 36,
                                    fontSize: 16,
                                    padding: '0 12px',
                                    border: '1px solid #CBCBCD',
                                    borderTopRightRadius: 6,
                                    borderBottomRightRadius: 6,
                                    outline: 'none',
                                },
                            }}
                        />
                        {showPhoneLoginButton && phoneError && (
                            <div className="login-error-row">
                                <svg
                                    className="error-icon"
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="20"
                                    height="20"
                                    fill="#e53e3e"
                                    viewBox="0 0 24 24"
                                >
                                    <circle cx="12" cy="12" r="10" fill="#e53e3e" />
                                    <path
                                        d="M12 8v4M12 16h.01"
                                        stroke="#fff"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        fill="none"
                                    />
                                </svg>
                                <span>{phoneError}</span>
                                <button
                                    className="login-btn"
                                    type="button"
                                    onClick={() =>
                                        navigate(`/login?phone=${encodeURIComponent(form.phonePrefix + form.phoneNumber)}`)
                                    }
                                >
                                    {t('1890', 'Log in') as string}
                                </button>
                            </div>
                        )}

                        {!showPhoneLoginButton && phoneError && (
                            <span className="error-text">
                                <svg className="error-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 24 24">
                                    <path
                                        stroke="#e53e3e"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M12 9v4M12 17h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9Z"
                                    />
                                </svg>
                                {phoneError}
                            </span>
                        )}

                    </div>
                </div>
            )}

            {settingsData?.fields?.profile?.gender?.visible && (

                <div className="row">
                    <div className="field full">
                        <label>{t('66', 'Gender') as string}
                            {settingsData?.fields?.profile?.gender?.required && <span className="required-star">*</span>}
                        </label>
                        <select
                            name="gender"
                            value={form.gender}
                            onChange={handleChange}
                            className={form.gender === '' ? 'placeholder' : ''}
                        >
                            <option value="">{t('8887', 'Select your gender') as string}</option>
                            {genderOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            )}

            {settingsData?.fields?.profile?.dateOfBirth?.visible && (
                <div className="row">
                    <div className="field full">
                        <label>{t('131', 'Date of birth') as string}
                            {settingsData?.fields?.profile?.dateOfBirth?.required && <span className="required-star">*</span>}
                        </label>
                        <div className="dob-input">
                            <select
                                name="dobDay"
                                value={form.dobDay}
                                onChange={handleChange}
                                className={form.dobDay === '' ? 'placeholder' : ''}
                            >
                                <option value="">DD</option>
                                {Array.from({ length: 31 }, (_, i) => (
                                    <option key={i} value={String(i + 1).padStart(2, '0')}>
                                        {String(i + 1).padStart(2, '0')}
                                    </option>
                                ))}
                            </select>

                            <select
                                name="dobMonth"
                                value={form.dobMonth}
                                onChange={handleChange}
                                className={form.dobMonth === '' ? 'placeholder' : ''}
                            >
                                <option value="">Month</option>
                                {Array.from({ length: 12 }, (_, i) => (
                                    <option key={i} value={String(i + 1).padStart(2, '0')}>
                                        {String(i + 1).padStart(2, '0')}
                                    </option>
                                ))}
                            </select>

                            <select
                                name="dobYear"
                                value={form.dobYear}
                                onChange={handleChange}
                                className={form.dobYear === '' ? 'placeholder' : ''}
                            >
                                <option value="">YYYY</option>
                                {Array.from({ length: 100 }, (_, i) => {
                                    const year = new Date().getFullYear() - i
                                    return (
                                        <option key={year} value={year}>
                                            {year}
                                        </option>
                                    )
                                })}
                            </select>
                        </div>
                    </div>
                </div>
            )}

            {settingsData?.fields?.profile?.address?.visible && (
                <div className="row">
                    <div className="field full">
                        <label>{t('358', 'Address') as string}
                            {settingsData?.fields?.profile?.address?.required && <span className="required-star">*</span>}
                        </label>
                        <GoogleAutocompleteStandalone
                            value={form.address}
                            onChange={(value: Values) => setForm(prev => ({ ...prev, address: value }))}
                            placeholder={t('358', 'Enter your address') as string}
                        />
                    </div>
                </div>
            )}

            <div className="row right">
                <button type="button" disabled={!isValid || isLoading} onClick={handleSubmit}>
                    {t('3035', 'Next') as string}
                </button>
            </div>
        </form>
    )
}

export default ApplyV2ProfileStep
